import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, ArrowLeft, Crown, Trophy, TrendingUp, Users, Calendar, UserCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Navigation from '../components/Navigation';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIOD_LABELS = { week: 'Semanal', month: 'Mensal', all: 'Total' };
const TAB_LIST = [
  { key: 'ranking', label: 'Ranking', icon: Trophy },
  { key: 'child', label: 'Por Criança', icon: UserCircle },
  { key: 'absences', label: 'Ausências', icon: AlertTriangle },
];

const MEDAL_COLORS = { 'Ouro': 'text-yellow-500', 'Prata': 'text-gray-400', 'Bronze': 'text-amber-700' };

const RelatorioGrupos = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [period, setPeriod] = useState('all');
  const [tab, setTab] = useState('ranking');
  const [report, setReport] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [childReport, setChildReport] = useState(null);
  const [selectedChild, setSelectedChild] = useState('');
  const [absencesReport, setAbsencesReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`);
        setGroups(res.data || []);
        if (res.data?.length > 0) setSelectedGroup(res.data[0].id);
      } catch { toast.error('Erro ao carregar grupos'); }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!selectedGroup) return;
    if (tab === 'ranking') { fetchReport(); fetchEvolution(); }
    if (tab === 'absences') fetchAbsences();
  }, [selectedGroup, period, tab]);

  useEffect(() => {
    if (selectedChild && selectedGroup && tab === 'child') fetchChildReport();
  }, [selectedChild, period, tab]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/groups/${selectedGroup}/report?period=${period}`);
      setReport(res.data);
      if (res.data?.children?.length > 0 && !selectedChild) setSelectedChild(res.data.children[0].child_id);
    } catch { toast.error('Erro ao carregar relatório'); }
    finally { setLoading(false); }
  };

  const fetchEvolution = async () => {
    try {
      const res = await axios.get(`${API}/groups/report/evolution/${selectedGroup}?weeks=8`);
      setEvolution(res.data);
    } catch {}
  };

  const fetchChildReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/groups/${selectedGroup}/report/child/${selectedChild}?period=${period}`);
      setChildReport(res.data);
    } catch { toast.error('Erro ao carregar relatório da criança'); }
    finally { setLoading(false); }
  };

  const fetchAbsences = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/groups/${selectedGroup}/report/absences?period=${period}`);
      setAbsencesReport(res.data);
    } catch { toast.error('Erro ao carregar relatório de ausências'); }
    finally { setLoading(false); }
  };

  const getPositionBadge = (pos) => {
    if (pos === 1) return <span className="bg-yellow-100 text-yellow-700 font-bold px-2 py-0.5 rounded-full text-xs flex items-center gap-1"><Trophy className="w-3 h-3" /> 1º</span>;
    if (pos === 2) return <span className="bg-gray-100 text-gray-600 font-bold px-2 py-0.5 rounded-full text-xs">2º</span>;
    if (pos === 3) return <span className="bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full text-xs">3º</span>;
    return <span className="text-gray-400 text-xs font-semibold">{pos}º</span>;
  };

  const getPresenceColor = (taxa) => {
    if (taxa >= 80) return 'text-green-600 bg-green-50';
    if (taxa >= 50) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  // Load ranking data when switching to child tab if not loaded
  useEffect(() => {
    if (tab === 'child' && !report && selectedGroup) fetchReport();
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Relatório de Grupos</h1>
          </div>
          <p className="font-inter text-white/70 text-sm">Acompanhe a evolução dos pré-adolescentes</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* Filtros */}
        <Card className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm" data-testid="filters-card">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Grupo de Discipulado</label>
              <select data-testid="select-group" value={selectedGroup} onChange={(e) => { setSelectedGroup(e.target.value); setSelectedChild(''); setReport(null); setChildReport(null); setAbsencesReport(null); }}
                className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm font-inter">
                {groups.map(g => (<option key={g.id} value={g.id}>{g.nome} ({(g.child_ids || []).length} crianças)</option>))}
              </select>
            </div>
            <div>
              <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Período</label>
              <div className="flex rounded-xl overflow-hidden border border-gray-200">
                {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                  <button key={key} data-testid={`period-${key}`} onClick={() => setPeriod(key)}
                    className={`px-4 py-2 text-sm font-semibold transition-all ${period === key ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Abas */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1" data-testid="tabs">
          {TAB_LIST.map(t => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === t.key ? 'bg-white text-[#1A4D2E] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <t.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden text-xs">{t.label}</span>
            </button>
          ))}
        </div>

        {loading && <div className="text-center py-12"><p className="font-inter text-gray-400">Carregando relatório...</p></div>}

        {/* =================== ABA RANKING =================== */}
        {tab === 'ranking' && report && !loading && (
          <>
            {/* Resumo */}
            <Card className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm" data-testid="group-summary">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-nunito font-bold text-xl text-[#1A4D2E]">{report.group?.nome}</h2>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {report.leader_names?.map((name, i) => (
                      <span key={i} className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
                        <Crown className="w-3 h-3" /> {name}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-[#1A4D2E]">
                    <Users className="w-4 h-4" />
                    <span className="font-nunito font-bold text-2xl">{report.total_children}</span>
                  </div>
                  <p className="text-xs text-gray-500">crianças</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Calendar className="w-3 h-3" />
                <span>Período: <strong>{PERIOD_LABELS[period]}</strong></span>
              </div>
            </Card>

            {/* Tabela de Ranking */}
            <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm overflow-hidden" data-testid="score-table">
              <div className="p-4 border-b border-[#E1ECE5]">
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] flex items-center gap-2"><Trophy className="w-5 h-5" /> Ranking e Pontuação</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F2F7F4] text-[#1A4D2E]">
                      <th className="px-3 py-2.5 text-left font-semibold">#</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Nome</th>
                      <th className="px-2 py-2.5 text-center font-semibold" title="Devocionais">Dev</th>
                      <th className="px-2 py-2.5 text-center font-semibold" title="Versículos">Ver</th>
                      <th className="px-2 py-2.5 text-center font-semibold" title="Presenças">Pre</th>
                      <th className="px-2 py-2.5 text-center font-semibold" title="Pontualidade">Pon</th>
                      <th className="px-2 py-2.5 text-center font-semibold" title="Bíblia">Bíb</th>
                      <th className="px-2 py-2.5 text-center font-semibold" title="Visitas">Vis</th>
                      <th className="px-2 py-2.5 text-center font-semibold" title="Gincana">Gin</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Total</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Medalha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.children?.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-gray-400">Nenhuma criança neste grupo</td></tr>}
                    {report.children?.map((child, idx) => (
                      <tr key={child.child_id} className={`border-t border-gray-100 ${idx < 3 ? 'bg-[#FAFBFA]' : ''}`}>
                        <td className="px-3 py-2.5">{getPositionBadge(child.posicao)}</td>
                        <td className="px-3 py-2.5 font-semibold text-[#1A4D2E] whitespace-nowrap">{child.nome}</td>
                        <td className="px-2 py-2.5 text-center">{child.total_devocionais}</td>
                        <td className="px-2 py-2.5 text-center">{child.total_versiculos}</td>
                        <td className="px-2 py-2.5 text-center">{child.total_presencas}</td>
                        <td className="px-2 py-2.5 text-center">{child.total_pontualidade}</td>
                        <td className="px-2 py-2.5 text-center">{child.total_biblia}</td>
                        <td className="px-2 py-2.5 text-center">{child.total_visitas}</td>
                        <td className="px-2 py-2.5 text-center">{child.total_gincana}</td>
                        <td className="px-3 py-2.5 text-center font-bold text-[#1A4D2E] text-base">{child.total_pontos}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`font-bold text-xs ${MEDAL_COLORS[child.medalha] || 'text-gray-300'}`}>{child.medalha || '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Gráfico de Evolução */}
            {evolution?.weeks?.length > 0 && (
              <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm p-4" data-testid="evolution-chart">
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5" /> Evolução Semanal — Média de Pontos</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={evolution.weeks}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E1ECE5" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#666' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E1ECE5' }} formatter={(value, name) => [value, name === 'media' ? 'Média' : 'Total']} labelFormatter={(label) => `Semana de ${label}`} />
                      <Bar dataKey="media" fill="#1A4D2E" radius={[6, 6, 0, 0]} name="Média" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </>
        )}

        {/* =================== ABA POR CRIANÇA =================== */}
        {tab === 'child' && !loading && (
          <>
            {/* Seletor de Criança */}
            <Card className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm" data-testid="child-selector">
              <label className="font-inter text-xs font-semibold text-gray-500 mb-1 block">Selecione a criança</label>
              <select data-testid="select-child" value={selectedChild}
                onChange={(e) => setSelectedChild(e.target.value)}
                className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm font-inter">
                {report?.children?.map(c => (
                  <option key={c.child_id} value={c.child_id}>{c.nome} — {c.total_pontos} pts ({c.posicao}º lugar)</option>
                ))}
              </select>
            </Card>

            {childReport && (
              <>
                {/* Ficha da Criança */}
                <Card className="rounded-xl p-5 border border-[#E1ECE5] bg-white shadow-sm" data-testid="child-card">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white text-xl font-bold">
                      {childReport.child.nome?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h2 className="font-nunito font-bold text-xl text-[#1A4D2E]">{childReport.child.nome}</h2>
                      <p className="text-sm text-gray-500">{childReport.child.idade} anos · Nº {childReport.child.numero}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{childReport.scores.total_pontos}</p>
                      <p className={`text-xs font-bold ${MEDAL_COLORS[childReport.scores.medalha] || 'text-gray-300'}`}>{childReport.scores.medalha || '—'}</p>
                    </div>
                  </div>

                  {/* Pontuação Detalhada */}
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {[
                      { l: 'Devocionais', v: childReport.scores.total_devocionais, c: 'bg-green-50 text-green-700' },
                      { l: 'Versículos', v: childReport.scores.total_versiculos, c: 'bg-blue-50 text-blue-700' },
                      { l: 'Presenças', v: childReport.scores.total_presencas, c: 'bg-teal-50 text-teal-700' },
                      { l: 'Pontualidade', v: childReport.scores.total_pontualidade, c: 'bg-indigo-50 text-indigo-700' },
                      { l: 'Bíblia', v: childReport.scores.total_biblia, c: 'bg-purple-50 text-purple-700' },
                      { l: 'Visitas', v: childReport.scores.total_visitas, c: 'bg-pink-50 text-pink-700' },
                      { l: 'Gincana', v: childReport.scores.total_gincana, c: 'bg-amber-50 text-amber-700' },
                    ].map((item, i) => (
                      <div key={i} className={`rounded-xl p-3 text-center ${item.c}`}>
                        <p className="text-[10px] font-semibold leading-tight mb-1">{item.l}</p>
                        <p className="font-bold text-lg">{item.v}</p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Gráfico Devocionais por Semana */}
                {childReport.weekly_devotionals?.length > 0 && (
                  <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm p-4" data-testid="child-devocional-chart">
                    <h3 className="font-nunito font-bold text-base text-[#1A4D2E] flex items-center gap-2 mb-4">
                      <TrendingUp className="w-4 h-4" /> Devocionais por Semana
                    </h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={childReport.weekly_devotionals}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E1ECE5" />
                          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#666' }} />
                          <YAxis tick={{ fontSize: 10, fill: '#666' }} domain={[0, 7]} />
                          <Tooltip contentStyle={{ borderRadius: '12px' }} formatter={(v) => [v, 'Devocionais']} labelFormatter={(l) => `Semana de ${l}`} />
                          <Bar dataKey="devocionais" fill="#2D8B5A" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}

                {/* Histórico de Presenças */}
                {childReport.presencas_hist?.length > 0 && (
                  <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm overflow-hidden" data-testid="child-presencas">
                    <div className="p-4 border-b border-[#E1ECE5]">
                      <h3 className="font-nunito font-bold text-base text-[#1A4D2E]">Histórico de Presenças</h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {childReport.presencas_hist.map((p, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-3">
                          <span className="text-sm text-gray-600">{p.data ? new Date(p.data + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                          <div className="flex gap-3">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.presenca ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                              {p.presenca ? 'Presente' : 'Ausente'}
                            </span>
                            {p.presenca && (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.pontualidade ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-600'}`}>
                                {p.pontualidade ? 'Pontual' : 'Atrasado'}
                              </span>
                            )}
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.biblia ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'}`}>
                              {p.biblia ? 'Bíblia' : 'Sem bíblia'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            )}
          </>
        )}

        {/* =================== ABA AUSÊNCIAS =================== */}
        {tab === 'absences' && absencesReport && !loading && (
          <>
            <Card className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm" data-testid="absences-header">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-nunito font-bold text-xl text-[#1A4D2E]">{absencesReport.group?.nome}</h2>
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Período: <strong>{PERIOD_LABELS[period]}</strong>
                  </p>
                </div>
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
            </Card>

            {/* Tabela de Ausências */}
            <Card className="rounded-xl border border-[#E1ECE5] bg-white shadow-sm overflow-hidden" data-testid="absences-table">
              <div className="p-4 border-b border-[#E1ECE5]">
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Frequência e Ausências
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F2F7F4] text-[#1A4D2E]">
                      <th className="px-4 py-2.5 text-left font-semibold">Nome</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Encontros</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Presenças</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Ausências</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Atrasos</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Taxa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absencesReport.summary?.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-gray-400">Nenhum registro encontrado</td></tr>
                    )}
                    {absencesReport.summary?.map((child) => (
                      <tr key={child.child_id} className="border-t border-gray-100">
                        <td className="px-4 py-3 font-semibold text-[#1A4D2E]">{child.nome}</td>
                        <td className="px-3 py-3 text-center">{child.total_encontros}</td>
                        <td className="px-3 py-3 text-center text-green-600 font-semibold">{child.total_presencas}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`font-bold ${child.total_ausencias > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {child.total_ausencias}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`${child.total_atrasos > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                            {child.total_atrasos}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`font-bold text-xs px-2 py-1 rounded-full ${getPresenceColor(child.taxa_presenca)}`}>
                            {child.taxa_presenca}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Cards Mobile Ausências */}
            <div className="sm:hidden space-y-3" data-testid="absences-mobile">
              {absencesReport.summary?.map((child) => (
                <Card key={child.child_id} className="rounded-xl p-4 border border-[#E1ECE5] bg-white shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-nunito font-bold text-[#1A4D2E]">{child.nome}</span>
                    <span className={`font-bold text-sm px-2 py-1 rounded-full ${getPresenceColor(child.taxa_presenca)}`}>
                      {child.taxa_presenca}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="text-[9px] text-gray-500">Encontros</p>
                      <p className="font-bold text-sm">{child.total_encontros}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                      <p className="text-[9px] text-green-600">Presenças</p>
                      <p className="font-bold text-sm text-green-700">{child.total_presencas}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2">
                      <p className="text-[9px] text-red-600">Ausências</p>
                      <p className="font-bold text-sm text-red-700">{child.total_ausencias}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2">
                      <p className="text-[9px] text-amber-600">Atrasos</p>
                      <p className="font-bold text-sm text-amber-700">{child.total_atrasos}</p>
                    </div>
                  </div>
                  {child.detalhes?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Datas de ausência:</p>
                      <div className="flex flex-wrap gap-1">
                        {child.detalhes.map((d, i) => (
                          <span key={i} className="bg-red-50 text-red-600 text-xs px-2 py-0.5 rounded">
                            {d.data ? new Date(d.data + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </>
        )}

        {!selectedGroup && groups.length === 0 && (
          <Card className="rounded-xl p-12 text-center border border-[#E1ECE5] bg-white">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500">Nenhum grupo de discipulado cadastrado</p>
            <Button onClick={() => navigate('/grupos')} className="mt-4 bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full">Criar Grupo</Button>
          </Card>
        )}
      </div>
      <Navigation active="relatorios" />
    </div>
  );
};

export default RelatorioGrupos;
